import {
  DeleteOutline,
  Payment,
  QuestionAnswer,
  Close,
  LocalShipping,
  Info,
} from '@mui/icons-material';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import { ShoppingListType } from '../../context/ShoppingListContext';
import ProductList from './ProductList';
import { DeliveryMethod } from '../../models/Order';
import { useOrder } from '../../context/OrderContext';
import Loading from '../global/loading/Loading';
import getIconForDeliveryMethod from '../../helperFunctions/getIconForDeliveryMethod';
import translateDevlieryMethod from '../../helperFunctions/translateDeliveryMethod';

type Props = {
  setIsShoppingListOpen: (isOpen: boolean) => void;
  shoppingList: ShoppingListType[];
  removeProductFromShoppingList: (productId: number) => void;
  setProductQuantity: (productId: number, quantity: number) => void;
  handleDialogOpen: () => void;
};

function ShoppingList({
  setIsShoppingListOpen,
  shoppingList,
  removeProductFromShoppingList,
  setProductQuantity,
  handleDialogOpen,
}: Props) {
  const navigate = useNavigate();
  const { getDeliveryOptions } = useOrder();

  const [deliveryCosts, setDeliveryCosts] = React.useState<
    { deliveryCost: number; deliveryMethod: DeliveryMethod }[] | null
  >(null);

  const [activeTab, setActiveTab] = React.useState<'cart' | 'summary'>('cart');
  const isEmpty = shoppingList.length === 0;

  useEffect(() => {
    const fetchDeliveryCosts = async () => {
      if (isEmpty) return;

      const costs = await getDeliveryOptions(
        shoppingList.map((product) => product.id),
      );
      setDeliveryCosts(costs);
    };

    fetchDeliveryCosts();
  }, [getDeliveryOptions, shoppingList, isEmpty]);

  const cartTotal = shoppingList.reduce(
    (acc, product) => acc + product.price * product.quantity,
    0,
  );

  const handleCheckout = () => {
    setIsShoppingListOpen(false);
    navigate('/koszyk/logowanie');
  };

  const handleClearCart = () => {
    shoppingList.forEach((product) =>
      removeProductFromShoppingList(product.id),
    );
  };

  return (
    <motion.div
      className="relative flex flex-col w-full h-full max-h-[85vh] overflow-hidden bg-white"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 sticky top-0 z-10 bg-white">
        <div className="flex items-center justify-between p-4">
          <h2 className="text-lg font-medium text-gray-900">
            Twój koszyk{' '}
            {!isEmpty && (
              <span className="text-primary">({shoppingList.length})</span>
            )}
          </h2>
          <motion.button
            whileHover={{ rotate: 90 }}
            transition={{ duration: 0.2 }}
            onClick={() => setIsShoppingListOpen(false)}
            className="rounded-full p-1 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <Close />
          </motion.button>
        </div>

        {/* Tabs */}
        {!isEmpty && (
          <div className="flex border-t border-gray-200">
            <motion.button
              className={`relative flex-1 py-3 text-center text-sm font-medium ${
                activeTab === 'cart'
                  ? 'text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('cart')}
              whileHover={{ backgroundColor: 'rgba(0,0,0,0.03)' }}
              whileTap={{ scale: 0.98 }}
            >
              Koszyk
              {activeTab === 'cart' && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                  layoutId="activeTabIndicator"
                />
              )}
            </motion.button>
            <motion.button
              className={`relative flex-1 py-3 text-center text-sm font-medium ${
                activeTab === 'summary'
                  ? 'text-primary'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('summary')}
              whileHover={{ backgroundColor: 'rgba(0,0,0,0.03)' }}
              whileTap={{ scale: 0.98 }}
            >
              Podsumowanie
              {activeTab === 'summary' && (
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                  layoutId="activeTabIndicator"
                />
              )}
            </motion.button>
          </div>
        )}
      </div>

      {/* Empty state */}
      {isEmpty && (
        <div className="flex-grow flex flex-col items-center justify-center p-6 text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-4 bg-gray-100 rounded-full p-4 inline-flex">
              <svg
                className="w-10 h-10 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                ></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Twój koszyk jest pusty
            </h3>
            <p className="text-gray-500 mb-6">
              Dodaj produkty do koszyka, aby rozpocząć zakupy
            </p>
            <motion.button
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
              onClick={() => setIsShoppingListOpen(false)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Kontynuuj zakupy
            </motion.button>
          </motion.div>
        </div>
      )}

      {/* Content */}
      {!isEmpty && (
        <div className="flex-grow overflow-y-auto pt-2">
          <AnimatePresence mode="wait">
            {activeTab === 'cart' && (
              <motion.div
                key="cart"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="px-4"
              >
                <ProductList
                  shoppingList={shoppingList}
                  removeProductFromShoppingList={removeProductFromShoppingList}
                  setProductQuantity={setProductQuantity}
                  setIsShoppingListOpen={setIsShoppingListOpen}
                />

                {/* Niestandardowe produkty */}
                {shoppingList.some((product) => !product.packaged) && (
                  <motion.div
                    className="flex items-start gap-3 p-3 mt-3 bg-amber-50 rounded-lg border border-amber-200 text-amber-800"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Info
                      className="text-amber-500 flex-shrink-0 mt-0.5"
                      fontSize="small"
                    />
                    <div className="text-xs">
                      <p className="font-medium mb-1">
                        Uwaga: Niestandardowe wymiary
                      </p>
                      <p>
                        Niektóre produkty nie mieszczą się w standardowych
                        gabarytach kurierskich.
                      </p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}

            {activeTab === 'summary' && (
              <motion.div
                key="summary"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="p-4"
              >
                <div className="space-y-6">
                  {/* Podsumowanie kosztów */}
                  <motion.div
                    className="bg-gray-50 rounded-lg p-4 shadow-sm"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      Podsumowanie zamówienia
                    </h3>

                    <div className="space-y-2 border-b border-gray-200 pb-4 mb-4">
                      <div className="flex justify-between">
                        <span className="text-gray-500">
                          Wartość produktów:
                        </span>
                        <span className="font-medium">
                          {cartTotal.toFixed(2)} zł
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Netto:</span>
                        <span className="text-gray-600">
                          {(cartTotal * 0.77).toFixed(2)} zł
                        </span>
                      </div>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="font-medium">Razem:</span>
                      <span className="text-lg font-bold text-primary">
                        {cartTotal.toFixed(2)} zł
                      </span>
                    </div>
                  </motion.div>

                  {/* Dostawa */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <div className="flex items-center mb-3">
                      <LocalShipping
                        className="text-gray-400 mr-2"
                        fontSize="small"
                      />
                      <h3 className="text-sm font-medium text-gray-700">
                        Dostawa
                      </h3>
                    </div>

                    {deliveryCosts === null ? (
                      <div className="p-6 flex justify-center">
                        <Loading />
                      </div>
                    ) : deliveryCosts.length === 0 ? (
                      <motion.div
                        className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-blue-700 text-sm"
                        initial={{ scale: 0.95 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        <p className="font-medium mb-1">
                          Niestandardowe wymiary
                        </p>
                        <p className="text-xs">
                          Niektóre produkty z twojej listy nie mogą być
                          dostarczone standardową metodą. Prosimy o złożenie
                          zapytania ofertowego, a przygotujemy indywidualną
                          ofertę.
                        </p>
                      </motion.div>
                    ) : (
                      <ul className="space-y-2">
                        {deliveryCosts.map((option, index) => (
                          <motion.li
                            key={option.deliveryMethod}
                            className="flex justify-between items-center p-3 border border-gray-200 rounded-md"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 + index * 0.1 }}
                            whileHover={{ backgroundColor: 'rgba(0,0,0,0.02)' }}
                          >
                            <div className="flex items-center">
                              <img
                                src={getIconForDeliveryMethod(
                                  option.deliveryMethod,
                                )}
                                alt={option.deliveryMethod}
                                className="h-6 mr-3"
                              />
                              <span className="text-sm">
                                {translateDevlieryMethod(option.deliveryMethod)}
                              </span>
                            </div>
                            <span className="font-medium">
                              {option.deliveryCost.toFixed(2)} zł
                            </span>
                          </motion.li>
                        ))}
                      </ul>
                    )}
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Footer */}
      {!isEmpty && (
        <div className="flex-shrink-0 border-t border-gray-200 p-4 space-y-3 mb-10">
          {/* Przycisk zapytania */}
          <motion.button
            onClick={() => {
              setIsShoppingListOpen(false);
              handleDialogOpen();
            }}
            className="flex items-center justify-center w-full py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            whileHover={{ y: -1, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
            whileTap={{ y: 0 }}
          >
            <QuestionAnswer className="mr-2" fontSize="small" />
            Zapytaj o produkty z listy
          </motion.button>

          <div className="grid grid-cols-2 gap-3">
            {/* Przycisk czyszczenia koszyka */}
            <motion.button
              onClick={handleClearCart}
              className="flex items-center justify-center py-2 px-4 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50"
              whileHover={{ y: -1, boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}
              whileTap={{ y: 0 }}
            >
              <DeleteOutline className="mr-1" fontSize="small" />
              Wyczyść
            </motion.button>

            {/* Przycisk płatności */}
            {activeTab === 'summary' &&
              deliveryCosts !== null &&
              deliveryCosts.length !== 0 && (
                <motion.button
                  onClick={handleCheckout}
                  className="flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                  whileHover={{ y: -1, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                  whileTap={{ y: 0 }}
                >
                  <Payment className="mr-1" fontSize="small" />
                  Do płatności
                </motion.button>
              )}

            {/* Przycisk przejścia do podsumowania */}
            {(activeTab === 'cart' ||
              (activeTab === 'summary' &&
                !(deliveryCosts !== null && deliveryCosts.length !== 0))) && (
              <motion.button
                onClick={() =>
                  activeTab === 'cart'
                    ? setActiveTab('summary')
                    : setActiveTab('cart')
                }
                className="flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                whileHover={{ y: -1, boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                whileTap={{ y: 0 }}
              >
                {activeTab === 'cart' ? 'Podsumowanie' : 'Wróć do koszyka'}
              </motion.button>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default ShoppingList;
